import { useEffect, useRef, useState } from 'react';

import dayjs from 'dayjs';

import { CELL_WIDTH_REM } from '@/modules/planner/constants';
import usePlannerStore from '@/modules/planner/store';

interface IProps {
  id: number;
  startDate: dayjs.Dayjs;
  endDate: dayjs.Dayjs;
  onDuplicate: (newStartDate: dayjs.Dayjs, newEndDate: dayjs.Dayjs) => void;
}

export const useDuplicateMode = ({ id, startDate, endDate, onDuplicate }: IProps) => {
  const zoomLevel = usePlannerStore(store => store.zoomLevel);
  const activeDuplicateId = usePlannerStore(store => store.activeDuplicateId);
  const setActiveDuplicateId = usePlannerStore(store => store.setActiveDuplicateId);
  const dates = usePlannerStore(store => store.dates);

  const isDuplicating = activeDuplicateId === id;
  const [duplicateOffset, setDuplicateOffset] = useState<number | null>(null);
  const duplicateOffsetRef = useRef<number | null>(null);
  const timelineRowRef = useRef<HTMLElement | null>(null);

  const cellSize = CELL_WIDTH_REM * zoomLevel;
  const duration = (endDate.startOf('day').diff(startDate.startOf('day'), 'day') + 1) * cellSize;
  const plannerFirstDate = dates[0];

  const toggleDuplicating = () => {
    if (!isDuplicating) {
      setActiveDuplicateId(id);
      setDuplicateOffset(null); // Start with null, set on first mouse move
      duplicateOffsetRef.current = null;
    } else {
      setActiveDuplicateId(null);
      setDuplicateOffset(null);
      duplicateOffsetRef.current = null;
    }
  };

  // Track mouse movement across timeline when duplicating
  useEffect(() => {
    if (!isDuplicating) return;

    // Cache rem size to avoid repeated getComputedStyle calls
    const remSize = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;

    const handleMouseMove = (e: MouseEvent) => {
      if (!timelineRowRef.current) return;

      // Stop propagation to prevent timeline panning
      e.stopPropagation();

      const rect = timelineRowRef.current.getBoundingClientRect();
      const xPx = e.clientX - rect.left;
      const xRem = xPx > 0 ? xPx / remSize : 0;

      // Calculate which day the cursor is over
      const dayOffset = Math.floor(xRem / cellSize);
      const newOffset = dayOffset * cellSize;

      // Only update state if offset actually changed (reduces re-renders)
      if (duplicateOffsetRef.current !== newOffset) {
        duplicateOffsetRef.current = newOffset;
        setDuplicateOffset(newOffset);
      }
    };

    const handleMouseDown = (e: MouseEvent) => {
      // Prevent timeline from starting any interactions
      e.stopPropagation();
      e.preventDefault();
    };

    const handleMouseUp = (e: MouseEvent) => {
      // Stop all propagation to prevent any click handlers
      e.stopPropagation();
      e.preventDefault();

      const finalOffset = duplicateOffsetRef.current;
      if (finalOffset !== null) {
        const durationInDays = endDate.startOf('day').diff(startDate.startOf('day'), 'day') + 1;
        const offsetInDays = finalOffset / cellSize;
        const newStartDate = plannerFirstDate.add(offsetInDays, 'day');
        const newEndDate = newStartDate.add(durationInDays - 1, 'day');

        onDuplicate(newStartDate, newEndDate);
        setActiveDuplicateId(null);
        setDuplicateOffset(null);
        duplicateOffsetRef.current = null;
      }
    };

    const handleClick = (e: MouseEvent) => {
      // Also stop click events to prevent any modal opening
      e.stopPropagation();
      e.preventDefault();
    };

    // Use capture phase like drag/resize - handle mousedown, mouseup, and click
    document.addEventListener('mousemove', handleMouseMove, { capture: true, passive: false });
    document.addEventListener('mousedown', handleMouseDown, { capture: true, passive: false });
    document.addEventListener('mouseup', handleMouseUp, { capture: true, passive: false });
    document.addEventListener('click', handleClick, { capture: true, passive: false });

    return () => {
      document.removeEventListener('mousemove', handleMouseMove, { capture: true });
      document.removeEventListener('mousedown', handleMouseDown, { capture: true });
      document.removeEventListener('mouseup', handleMouseUp, { capture: true });
      document.removeEventListener('click', handleClick, { capture: true });
    };
  }, [
    isDuplicating,
    cellSize,
    plannerFirstDate,
    endDate,
    startDate,
    onDuplicate,
    setActiveDuplicateId,
  ]);

  // Escape key handler to cancel duplicate mode
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isDuplicating) {
        setActiveDuplicateId(null);
        setDuplicateOffset(null);
        duplicateOffsetRef.current = null;
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isDuplicating, setActiveDuplicateId]);

  const setTimelineRow = (element: HTMLElement | null) => {
    timelineRowRef.current = element;
  };

  return {
    isDuplicating,
    duplicateOffset,
    duration,
    toggleDuplicating,
    setTimelineRow,
  };
};
